import { Component, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  MenuItem,
  Pagination,
  Popover,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  FormControlLabel,
  Switch,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import RefreshIcon from '@mui/icons-material/Refresh';
import AccessAlarmIcon from '@mui/icons-material/AccessAlarm';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ScheduleSendIcon from '@mui/icons-material/ScheduleSend';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import SendIcon from '@mui/icons-material/Send';
import SaveIcon from '@mui/icons-material/Save';
import TaskIcon from '@mui/icons-material/Task';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';

dayjs.extend(relativeTime);
import { TextEditor } from '@src/components/core/text-editor/text-editor';
import ThreadMessage from '@src/components/client-inbox/ThreadMessage';
import ComposerReport from '@src/components/client-inbox/ComposerReport';
import ThreadAgentPanel from '@src/components/client-inbox/ThreadAgentPanel';
import AutonomyReportDialog from '@src/components/client-inbox/AutonomyReportDialog';
import UnrunActionDialog, { findUnrunStaffAction } from '@src/components/client-inbox/UnrunActionDialog';
import {
  AutosendBanner,
  ComposerStatusChips,
  InvestigationBanner,
  StaffInstructionHistory,
} from '@src/components/client-inbox/ThreadAutomationStatus';
import {
  WORK_DECISION_META,
  buildReplyRecipients,
  escapeHtml,
  formatWorkDecisionLabel,
  normalizeAddresses,
  parseAddressInput,
} from '@src/components/client-inbox/emailHelpers';

class InboxErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    console.error('InboxErrorBoundary caught:', error, info?.componentStack);
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 32, textAlign: 'center' }}>
          <h3>Something went wrong</h3>
          <pre style={{ textAlign: 'left', whiteSpace: 'pre-wrap', color: 'red', fontSize: 12, maxHeight: 300, overflow: 'auto' }}>
            {this.state.error?.toString()}
            {'\n\n'}
            {this.state.error?.stack}
          </pre>
          <button onClick={() => this.setState({ error: null })} style={{ marginTop: 16, padding: '8px 16px' }}>
            Try Again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

import { Autocomplete } from '@mui/material';
import { fetchClients } from '@src/api/actions/clients';
import { createTask } from '@src/api/tasks';
import clientInboxApi from '@src/api/clientInbox';
import { useMessage } from '@src/context/common';
import useClientAutocomplete from '@src/hooks/useClientAutocomplete';

function ParentLevelAssignBanner({ thread, clients, onAssigned, onError }) {
  const parentCompanyId = thread.parent_company;
  const suggestion = thread.suggested_child_client;
  const childOptions = useMemo(() => {
    if (!parentCompanyId || !Array.isArray(clients)) return [];
    return clients.filter(
      (c) => String(c.parent_company_id ?? '') === String(parentCompanyId ?? ''),
    );
  }, [clients, parentCompanyId]);

  const [selectedClientId, setSelectedClientId] = useState(
    suggestion?.client_id ? String(suggestion.client_id) : '',
  );
  const [submitting, setSubmitting] = useState(false);

  const submit = useCallback(
    (clientIdOverride) => {
      const targetId = clientIdOverride ?? selectedClientId;
      if (!targetId) return;
      setSubmitting(true);
      clientInboxApi
        .reassignThreadToClient(thread.id, { client_id: Number(targetId), rerun_analysis: true })
        .then((res) => {
          onAssigned?.(res.data);
        })
        .catch((err) => {
          onError?.(err);
        })
        .finally(() => setSubmitting(false));
    },
    [thread.id, selectedClientId, onAssigned, onError],
  );

  return (
    <Alert
      severity="info"
      variant="outlined"
      sx={{ mt: 1 }}
      icon={<LightbulbOutlinedIcon />}
    >
      <Stack spacing={1}>
        <Typography variant="subtitle2">
          Filed under the parent company{' '}
          <strong>{thread.parent_company_name || 'Unknown parent'}</strong>. You can reply
          as is; move it to a branch only if it's about one.
        </Typography>
        {suggestion?.client_id ? (
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="body2">
              AI suggests: <strong>{suggestion.client_name || 'Unknown'}</strong>
              {typeof suggestion.confidence === 'number'
                ? ` (${Math.round(suggestion.confidence * 100)}% confidence)`
                : ''}
              {suggestion.reason ? ` — ${suggestion.reason}` : ''}
            </Typography>
            <Button
              size="small"
              variant="contained"
              color="primary"
              disabled={submitting}
              onClick={() => submit(String(suggestion.client_id))}
            >
              Accept
            </Button>
          </Stack>
        ) : null}
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <FormControl size="small" sx={{ minWidth: 240 }}>
            <InputLabel>Move to branch</InputLabel>
            <Select
              label="Move to branch"
              value={selectedClientId}
              onChange={(e) => setSelectedClientId(e.target.value)}
            >
              <MenuItem value="">
                <em>Select a client...</em>
              </MenuItem>
              {childOptions.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            size="small"
            variant="outlined"
            disabled={!selectedClientId || submitting}
            onClick={() => submit()}
          >
            Move
          </Button>
        </Stack>
      </Stack>
    </Alert>
  );
}

const STATUS_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'pending', label: 'Pending', color: 'default' },
  { value: 'analyzing', label: 'Analyzing', color: 'info' },
  { value: 'analyzed', label: 'Analyzed', color: 'success' },
  { value: 'task_created', label: 'Task created', color: 'primary' },
  { value: 'ignored', label: 'Ignored', color: 'warning' },
  { value: 'failed', label: 'Failed', color: 'error' },
];

const TASK_FILTER_OPTIONS = [
  { value: 'all', label: 'All emails' },
  { value: 'without', label: 'Needs task' },
  { value: 'with', label: 'Linked to task' },
];

const CONVERSATION_STATUS_OPTIONS = [
  { value: 'all', label: 'All', color: 'default' },
  { value: 'needs_client_assignment', label: 'Needs Client Assignment', color: 'warning' },
  { value: 'needs_response', label: 'Needs Response', color: 'error' },
  { value: 'in_progress', label: 'In Progress', color: 'info' },
  { value: 'awaiting_client', label: 'Awaiting Client', color: 'warning' },
  { value: 'closed', label: 'Closed', color: 'default' },
  { value: 'snoozed', label: 'Snoozed', color: 'secondary' },
  { value: 'reporting', label: 'Reporting', color: 'default' },
];

const CONVERSATION_STATUS_LABEL_MAP = CONVERSATION_STATUS_OPTIONS.reduce((map, opt) => {
  map[opt.value] = opt.label;
  return map;
}, {});

const CONVERSATION_STATUS_COLOR_MAP = CONVERSATION_STATUS_OPTIONS.reduce((map, opt) => {
  if (opt.color) map[opt.value] = opt.color;
  return map;
}, {});

// Background "Regenerate draft" jobs (one per thread). Active ones are polled
// until they land; READY stays on the row until the thread is opened.
const REGEN_ACTIVE = new Set(['queued', 'running']);
const REGEN_POLL_MS = 5000;
const REGEN_CHIP = {
  queued: { label: 'Regenerating…', color: 'info', variant: 'outlined' },
  running: { label: 'Regenerating…', color: 'info', variant: 'outlined' },
  ready: { label: 'Draft ready', color: 'success', variant: 'filled' },
  failed: { label: 'Draft failed', color: 'error', variant: 'outlined' },
};

// Ordered status filter pills shown on both mobile and desktop. `value` matches a
// conversation_status (or 'all'); `countKey` maps to a dashboard-summary count.
const STATUS_FILTER_PILLS = [
  { value: 'all', label: 'All', countKey: 'total_open', color: 'primary' },
  { value: 'needs_response', label: 'Needs Response', countKey: 'needs_response', color: 'error' },
  { value: 'needs_client_assignment', label: 'Needs Client', countKey: 'needs_client_assignment', color: 'warning' },
  { value: 'in_progress', label: 'In Progress', countKey: 'in_progress', color: 'info' },
  { value: 'awaiting_client', label: 'Awaiting', countKey: 'awaiting_client', color: 'warning' },
  { value: 'snoozed', label: 'Snoozed', countKey: 'snoozed', color: 'secondary' },
  { value: 'reporting', label: 'Reporting', countKey: 'reporting', color: 'default' },
  // No count on Closed: this filters ALL closed threads, but the only closed
  // count we have is "closed today" (shown separately in the metrics strip).
  { value: 'closed', label: 'Closed', countKey: null, color: 'default' },
];

// Nearly every client user is an Admin; Viewer exists for the occasional
// read-only stakeholder. Ordered so the common choice sits first.
const PORTAL_ROLE_OPTIONS = [
  { value: 'admin', label: 'Admin' },
  { value: 'editor', label: 'Editor' },
  { value: 'viewer', label: 'Viewer' },
];

function SuggestedActionCard({
  action,
  index,
  staffAction,
  running,
  roleValue,
  onRoleChange,
  onRun,
}) {
  if (!action) {
    return null;
  }

  if (typeof action === 'string') {
    return (
      <Box px={2} py={1} borderRadius={1} bgcolor="grey.100">
        <Typography variant="body2">{action}</Typography>
      </Box>
    );
  }

  if (typeof action !== 'object') {
    return null;
  }

  const { title, summary, description, owner, due_date: dueDate } = action;
  const preflight = staffAction?.preflight || {};
  const executed = Boolean(staffAction?.executed);
  const blocked = Boolean(preflight.blocked);
  const showRolePicker = (staffAction?.allowed_overrides || []).includes('role');
  const preflightWarnings = Array.isArray(preflight.warnings) ? preflight.warnings.filter(Boolean) : [];

  return (
    <Box
      px={2}
      py={1.5}
      borderRadius={1}
      bgcolor={executed ? 'success.50' : 'grey.100'}
      border={executed ? '1px solid' : undefined}
      borderColor={executed ? 'success.light' : undefined}
      display="flex"
      flexDirection="column"
      gap={0.5}
    >
      {title ? (
        <Typography variant="subtitle2" fontWeight={600}>
          {title}
        </Typography>
      ) : null}
      {summary ? <Typography variant="body2">{summary}</Typography> : null}
      {description ? (
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
      ) : null}
      {owner || dueDate ? (
        <Typography variant="caption" color="text.secondary">
          {owner ? `Owner: ${owner}` : ''}
          {owner && dueDate ? ' • ' : ''}
          {dueDate ? `Due: ${dayjs(dueDate).format('MMM D, YYYY')}` : ''}
        </Typography>
      ) : null}

      {executed ? (
        <Stack direction="row" spacing={0.75} alignItems="center" mt={0.5}>
          <CheckIcon sx={{ fontSize: 16, color: 'success.main' }} />
          <Typography variant="caption" color="success.dark" fontWeight={600}>
            {staffAction.execution?.summary || 'Done'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {staffAction.execution?.executed_at
              ? `• ${dayjs(staffAction.execution.executed_at).format('MMM D, h:mm A')}`
              : ''}
            {staffAction.execution?.executed_by
              ? ` by ${staffAction.execution.executed_by}`
              : ''}
          </Typography>
        </Stack>
      ) : null}

      {staffAction && !executed ? (
        <Stack spacing={0.75} mt={0.75}>
          {preflight.summary ? (
            <Typography variant="caption" color="text.primary">
              {preflight.summary}
            </Typography>
          ) : null}
          {preflight.description ? (
            <Typography variant="caption" color="text.secondary">
              {preflight.description}
            </Typography>
          ) : null}
          {preflight.message ? (
            <Typography
              variant="caption"
              color={blocked ? 'warning.dark' : 'text.secondary'}
            >
              {preflight.message}
            </Typography>
          ) : null}
          {preflightWarnings.map((warning) => (
            <Typography key={warning} variant="caption" color="warning.dark">
              {warning}
            </Typography>
          ))}
          {blocked ? null : (
            <Stack direction="row" spacing={1} alignItems="center">
              {showRolePicker ? (
                <FormControl size="small" sx={{ minWidth: 108 }}>
                  <Select
                    value={roleValue}
                    onChange={(event) => onRoleChange(event.target.value)}
                    disabled={running}
                    sx={{ fontSize: 13 }}
                  >
                    {PORTAL_ROLE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value} sx={{ fontSize: 13 }}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : null}
              <Button
                size="small"
                variant="contained"
                disableElevation
                disabled={running}
                startIcon={
                  running ? <CircularProgress size={14} color="inherit" /> : <SendIcon sx={{ fontSize: 14 }} />
                }
                onClick={() => onRun(staffAction, index)}
                sx={{ textTransform: 'none', fontWeight: 600, fontSize: 13 }}
              >
                {running ? 'Working…' : preflight.verb || staffAction.label}
              </Button>
            </Stack>
          )}
        </Stack>
      ) : null}
    </Box>
  );
}

function getInitialFilters() {
  return {
    search: '',
    status: 'all',
    conversation_status: 'all',
    client: null,
    task: 'all',
    owner: 'all',
  };
}

const BUDGET_NOTE_BLOCK_REGEX =
  /<!-- budget-note:start -->[\s\S]*?<!-- budget-note:end -->|<p[^>]*data-budget-note=\"true\"[^>]*>[\s\S]*?<\/p>|<p[^>]*class=\"budget-note-line\"[^>]*>[\s\S]*?<\/p>/gi;
const ELLIPSIS_ARTIFACT_REGEX = /<(p|div|span)[^>]*>\s*(?:\.{3}|…)\s*<\/\1>/gi;

function upsertBudgetNote(html, note) {
  const base = String(html || '')
    .replace(BUDGET_NOTE_BLOCK_REGEX, '')
    .replace(ELLIPSIS_ARTIFACT_REGEX, '');
  const trimmed = String(note || '').trim();
  if (!trimmed) {
    return base;
  }

  const noteHtml = escapeHtml(trimmed).replace(/\n/g, '<br>');
  const block = [
    '<p class="budget-note-line" style="margin:14px 0 0;font-size:15px;line-height:1.6;color:#374151;">',
    noteHtml,
    '</p>',
  ].join('');

  const anchor = 'If you want to keep the same budget for';
  const anchorIndex = base.indexOf(anchor);
  if (anchorIndex === -1) {
    return `${base}${block}`;
  }

  const insertAt = base.lastIndexOf('<p', anchorIndex);
  if (insertAt === -1) {
    return `${base}${block}`;
  }
  return `${base.slice(0, insertAt)}${block}${base.slice(insertAt)}`;
}


function ClientInboxPage() {
  const message = useMessage();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const [mobilePanel, setMobilePanel] = useState('queue'); // 'queue' | 'detail'
  const [filtersDrawerOpen, setFiltersDrawerOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const threadParam = searchParams.get('thread') || '';
  const [searchInput, setSearchInput] = useState('');
  const [filters, setFilters] = useState(getInitialFilters);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [clients, setClients] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedMessageDetail, setSelectedMessageDetail] = useState(null);
  const [selectedId, setSelectedId] = useState(() => {
    const raw = searchParams.get('thread');
    const parsed = Number(raw);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  });
  const [draftValue, setDraftValue] = useState('');
  const [replyFromValue, setReplyFromValue] = useState('');
  const [replySubject, setReplySubject] = useState('');
  const [replyToValue, setReplyToValue] = useState('');
  const [replyCcValue, setReplyCcValue] = useState('');
  const [senderOptions, setSenderOptions] = useState([]);
  const [defaultSender, setDefaultSender] = useState('');
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [creatingTask, setCreatingTask] = useState(false);
  const [sendingReply, setSendingReply] = useState(false);
  const [error, setError] = useState('');
  const [budgetNote, setBudgetNote] = useState('');
  const [regenerateLoading, setRegenerateLoading] = useState(false);
  const [runningActionKey, setRunningActionKey] = useState('');
  const [actionRoleOverrides, setActionRoleOverrides] = useState({});
  const [pendingStaffAction, setPendingStaffAction] = useState(null);
  const [isListening, setIsListening] = useState(false);
  const [voiceTarget, setVoiceTarget] = useState(null); // 'draft' while dictating
  const [interimVoiceText, setInterimVoiceText] = useState('');
  const [polishLoading, setPolishLoading] = useState(false);
  const [composerReport, setComposerReport] = useState(null);
  // "My message stands": full polish, but nothing in the notes may be
  // contradicted, softened, dropped, or followed by a policy redirect.
  const [keepWording, setKeepWording] = useState(false);
  const recognitionRef = useRef(null);
  const draftEditorRef = useRef(null);
  // The thread the composer is showing right now. Async replies (polish,
  // regenerate) compare against it before touching state, so a response
  // that lands after the staff member has moved on to another thread cannot
  // write the previous thread's draft into this one.
  const selectedIdRef = useRef(null);
  const [dashboardSummary, setDashboardSummary] = useState(null);
  const [selectedThreadIds, setSelectedThreadIds] = useState([]);
  const [bulkClosing, setBulkClosing] = useState(false);
  const [bulkRegenerating, setBulkRegenerating] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeSending, setComposeSending] = useState(false);
  const [forwardOpen, setForwardOpen] = useState(false);
  const [forwardSending, setForwardSending] = useState(false);
  const [replyAttachments, setReplyAttachments] = useState([]);
  const [undoSendData, setUndoSendData] = useState(null);
  const [scheduleAnchor, setScheduleAnchor] = useState(null);
  const [scheduledReplies, setScheduledReplies] = useState([]);
  const [cancellingAutosend, setCancellingAutosend] = useState(false);
  const [autonomyOpen, setAutonomyOpen] = useState(false);
  const [unrunSendAction, setUnrunSendAction] = useState(null);
  const agentPanelRef = useRef(null);
  const undoTimerRef = useCallback(() => {}, []);
  const {
    options: clientOptions,
    loading: clientsLoading,
    handleInputChange: handleClientSearch,
  } = useClientAutocomplete();
  const selectedClientOption = useMemo(
    () => clientOptions.find((opt) => String(opt.value) === String(filters.client)) || null,
    [clientOptions, filters.client],
  );


  const selectedMessage = useMemo(
    () => selectedMessageDetail || messages.find((item) => item.id === selectedId) || null,
    [messages, selectedId, selectedMessageDetail],
  );
  // While a background regeneration owns this thread's draft, the composer is
  // read-only: anything typed now would be replaced when the new draft lands.
  const selectedRegenActive = REGEN_ACTIVE.has(selectedMessage?.draft_regen_status);

  useEffect(() => {
    const parsed = Number(threadParam);
    const nextId = Number.isFinite(parsed) && parsed > 0 ? parsed : null;
    if (nextId !== selectedId) {
      setSelectedId(nextId);
    }
  }, [threadParam]);

  useEffect(() => {
    if (threadParam) {
      return;
    }
    if (selectedId && !messages.some((item) => item.id === selectedId)) {
      setSelectedId(null);
    }
  }, [messages, selectedId, threadParam]);

  useEffect(() => {
    const current = threadParam;
    const next = selectedId ? String(selectedId) : '';
    if (next && current !== next) {
      const params = new URLSearchParams(searchParams.toString());
      params.set('thread', next);
      setSearchParams(params, { replace: true });
      return;
    }
    if (!next && current) {
      const params = new URLSearchParams(searchParams.toString());
      params.delete('thread');
      setSearchParams(params, { replace: true });
    }
  }, [searchParams, selectedId, setSearchParams, threadParam]);
  const selectedAnalysisPayload = selectedMessage?.analysis_payload || {};
  const selectedQuestionRouter = selectedAnalysisPayload.question_router || null;
  const workDecision = (
    selectedAnalysisPayload.work_decision || selectedQuestionRouter?.work_decision || ''
  )
    .toString()
    .trim()
    .toLowerCase();
  const workDecisionMeta = WORK_DECISION_META[workDecision] || null;
  const sortedThreadMessages = useMemo(
    () => (selectedMessage?.messages || []).slice().sort((a, b) => {
      const ta = new Date(a.received_at || a.created_at).getTime();
      const tb = new Date(b.received_at || b.created_at).getTime();
      if (ta !== tb) return tb - ta;
      return (b.id || 0) - (a.id || 0);
    }),
    [selectedMessage?.messages],
  );
  const latestThreadMessage = sortedThreadMessages[0] || null;
  const earlierThreadMessages = sortedThreadMessages.slice(1);
  const sendBlocked = false;
  const createTaskRecommended =
    workDecision === 'reply_and_task' ||
    workDecision === 'task_only' ||
    workDecision === 'needs_human_review';
  const isLockedBudgetConfirmationDraft = useMemo(() => {
    const processingNotes = String(selectedMessage?.processing_notes || '').toLowerCase();
    return processingNotes.includes('budget confirmation draft');
  }, [selectedMessage]);

  const draftChanged = useMemo(() => {
    if (!selectedMessage) {
      return false;
    }
    if (isLockedBudgetConfirmationDraft) {
      return upsertBudgetNote(draftValue, budgetNote) !== (selectedMessage.reply_draft || '');
    }
    return draftValue !== (selectedMessage.reply_draft || '');
  }, [budgetNote, draftValue, isLockedBudgetConfirmationDraft, selectedMessage]);

  const taskFilterValue = filters.task;

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setFilters((prev) => {
        if (prev.search === searchInput) {
          return prev;
        }
        return { ...prev, search: searchInput };
      });
      setPage(1);
    }, 250);
    return () => window.clearTimeout(timeoutId);
  }, [searchInput]);

  const handleFilterChange = useCallback((field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPage(1);
  }, []);

  // Count of active secondary filters (everything except status pills + search),
  // used for the mobile filter-button badge.
  const secondaryFilterCount = useMemo(
    () =>
      (filters.client ? 1 : 0) +
      (filters.owner !== 'all' ? 1 : 0) +
      (filters.task !== 'all' ? 1 : 0) +
      (filters.status !== 'all' ? 1 : 0),
    [filters.client, filters.owner, filters.task, filters.status],
  );

  const loadClients = useCallback(() => {
    fetchClients()
      .then((response) => {
        const data = response.data?.results || response.data || [];
        const normalized = (Array.isArray(data) ? data : []).map((client) => ({
          id: String(client.id),
          name: client.name || 'Unnamed client',
          parent_company_id: client.parent_company ?? client.parent_company_id ?? null,
        }));
        normalized.sort((a, b) => a.name.localeCompare(b.name));
        setClients(normalized);
      })
      .catch((err) => {
        console.error('Failed to load clients for inbox filters', err);
        message.error('Unable to load clients for the inbox filter.');
      });
  }, [message]);

  const loadMessages = useCallback(() => {
    setLoading(true);
    setError('');
    const params = {
      page,
      ordering: '-received_at',
    };
    if (pageSize) {
      params.page_size = pageSize;
    }

    if (filters.search) {
      params.search = filters.search;
    }
    if (filters.status && filters.status !== 'all') {
      params.processing_status = filters.status;
    }
    if (filters.conversation_status && filters.conversation_status !== 'all') {
      params.conversation_status = filters.conversation_status;
    }
    if (filters.client) {
      params.client = filters.client;
    }
    if (taskFilterValue === 'with') {
      params.has_task = 'true';
    } else if (taskFilterValue === 'without') {
      params.has_task = 'false';
    }
    if (filters.owner === 'me') {
      params.owner = 'me';
    }

    clientInboxApi
      .listEmailMessages(params)
      .then((response) => {
        const payload = response.data;
        const results = Array.isArray(payload)
          ? payload
          : payload?.results || [];
        const count = Array.isArray(payload)
          ? payload.length
          : payload?.count ?? results.length;

        setMessages(results);
        setTotalCount(count);
        if (Array.isArray(payload)) {
          const derived = results.length || pageSize || 25;
          if (derived !== pageSize) {
            setPageSize(derived);
          }
        } else if (payload?.page_size && payload.page_size !== pageSize) {
          setPageSize(payload.page_size);
        }
        setSelectedId((current) => {
          if (results.length === 0) {
            setSelectedMessageDetail(null);
            return null;
          }
          const urlSelectedId = (() => {
            const parsed = Number(threadParam);
            return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
          })();
          if (urlSelectedId) {
            return urlSelectedId;
          }
          return results.some((item) => item.id === current)
            ? current
            : results[0].id;
        });
      })
      .catch((err) => {
        console.error('Failed to load client inbox messages', err);
        setError('Unable to load client inbox messages.');
        message.error('Unable to load client inbox messages.');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [filters.client, filters.conversation_status, filters.owner, filters.search, filters.status, message, page, pageSize, taskFilterValue, threadParam]);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  useEffect(() => {
    clientInboxApi
      .listEmailSenders()
      .then((response) => {
        const senders = response?.data?.senders || [];
        const resolvedDefault = response?.data?.default || '';
        setSenderOptions(senders);
        setDefaultSender(resolvedDefault);
        setReplyFromValue((current) => {
          if (current && senders.includes(current)) {
            return current;
          }
          if (resolvedDefault && senders.includes(resolvedDefault)) {
            return resolvedDefault;
          }
          return senders[0] || '';
        });
      })
      .catch((err) => {
        console.error('Failed to load available senders', err);
        setSenderOptions([]);
        message.error('Unable to load available sender addresses.');
      });
  }, [message]);

  const loadDashboardSummary = useCallback(() => {
    const params = {};
    if (filters.client) params.client = filters.client;
    clientInboxApi.getDashboardSummary(params)
      .then((response) => setDashboardSummary(response.data || null))
      .catch(() => {});
  }, [filters.client]);

  useEffect(() => {
    loadMessages();
    loadDashboardSummary();
  }, [loadMessages, loadDashboardSummary]);

  // Auto-save draft every 3 seconds after user stops typing
  const autoSaveRef = useCallback(
    (() => {
      let timer = null;
      let lastSaved = '';
      return (threadId, value) => {
        if (timer) clearTimeout(timer);
        if (!threadId || !value || value === lastSaved) return;
        timer = setTimeout(() => {
          clientInboxApi.updateEmailMessage(threadId, { reply_draft: value })
            .then(() => { lastSaved = value; })
            .catch(() => {});
        }, 3000);
      };
    })(),
    [],
  );

  useEffect(() => {
    if (selectedId && draftValue) {
      autoSaveRef(selectedId, draftValue);
    }
  }, [selectedId, draftValue, autoSaveRef]);

  // Per-thread feedback panel. The composer report ("looked up", "numbers
  // checked") describes one thread's draft, and nothing else clears it, so without this a thread switch left the
  // previous client's lookups sitting under the next client's email.
  useEffect(() => {
    selectedIdRef.current = selectedId;
    setComposerReport(null);
  }, [selectedId]);

  useEffect(() => {
    if (!selectedId) {
      setSelectedMessageDetail(null);
      return undefined;
    }
    let active = true;
    setSelectedMessageDetail(null);
    setDetailLoading(true);
    clientInboxApi.markThreadRead(selectedId).catch(() => {});
    // mark-read also clears a "Draft ready" badge server-side; match it here.
    setMessages((prev) =>
      prev.map((item) =>
        item.id === selectedId && item.draft_regen_status === 'ready' ? { ...item, draft_regen_status: '' } : item,
      ),
    );
    clientInboxApi
      .retrieveEmailMessage(selectedId)
      .then((response) => {
        if (!active) {
          return;
        }
        setSelectedMessageDetail(response.data || null);
      })
      .catch((err) => {
        if (!active) {
          return;
        }
        console.error('Failed to load client inbox message detail', err);
        message.error('Unable to load the full email detail.');
        setSelectedMessageDetail(null);
      })
      .finally(() => {
        if (active) {
          setDetailLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [message, selectedId]);

  useEffect(() => {
    if (selectedMessage) {
      const nextDraft = selectedMessage.reply_draft || '';
      setDraftValue(nextDraft);
      setBudgetNote('');
      const baseSubject = selectedMessage.subject || 'Client email';
      setReplySubject(baseSubject);
      const sender = isLockedBudgetConfirmationDraft && defaultSender ? defaultSender : replyFromValue;
      const recipients = buildReplyRecipients(selectedMessage, sender);
      setReplyToValue(recipients.to);
      setReplyCcValue(recipients.cc);
      if (isLockedBudgetConfirmationDraft && defaultSender) {
        setReplyFromValue(defaultSender);
      }
    } else {
      setDraftValue('');
      setBudgetNote('');
      setReplySubject('');
      setReplyToValue('');
      setReplyCcValue('');
    }
  }, [defaultSender, isLockedBudgetConfirmationDraft, replyFromValue, selectedMessage]);

  // Re-compute CC when sender changes (auto-CC partner)
  useEffect(() => {
    if (selectedMessage && replyFromValue) {
      const recipients = buildReplyRecipients(selectedMessage, replyFromValue);
      setReplyCcValue(recipients.cc);
    }
  }, [replyFromValue, selectedMessage]);

  const handleToggleThread = useCallback((threadId) => {
    setSelectedThreadIds((prev) =>
      prev.includes(threadId) ? prev.filter((id) => id !== threadId) : [...prev, threadId],
    );
  }, []);

  const handleSelectAllThreads = useCallback(() => {
    setSelectedThreadIds((prev) =>
      prev.length === messages.length ? [] : messages.map((m) => m.id),
    );
  }, [messages]);

  const handleBulkClose = useCallback(() => {
    if (selectedThreadIds.length === 0) return;
    setBulkClosing(true);
    clientInboxApi
      .bulkAction({ action: 'close', thread_ids: selectedThreadIds })
      .then(() => {
        message.success(`Closed ${selectedThreadIds.length} thread${selectedThreadIds.length > 1 ? 's' : ''}`);
        setSelectedThreadIds([]);
        loadMessages();
        loadDashboardSummary();
      })
      .catch((err) => {
        console.error('Bulk close failed', err);
        message.error('Failed to close selected threads.');
      })
      .finally(() => setBulkClosing(false));
  }, [loadDashboardSummary, loadMessages, message, selectedThreadIds]);

  // Clear selection when messages change (e.g. filter/page change)
  useEffect(() => {
    setSelectedThreadIds([]);
  }, [filters, page]);

  const toggleVoiceInput = useCallback(
    (target = 'draft') => {
      if (isListening) {
        recognitionRef.current?.stop();
        return;
      }
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!SpeechRecognition) {
        message.warning('Speech recognition is not supported in this browser.');
        return;
      }
      const recognition = new SpeechRecognition();
      recognition.lang = 'en-US';
      recognition.interimResults = true;
      recognition.continuous = true;
      recognitionRef.current = recognition;

      const isDraft = target === 'draft';
      const setter = setDraftValue;

      setVoiceTarget(target);
      setInterimVoiceText('');

      const appendFinal = (text) => {
        const trimmed = text.trim();
        if (!trimmed) return;
        if (isDraft) {
          // Append through the editor so the user's cursor and selection stay
          // put while dictation continues. Falling back to the controlled-prop
          // path would call setContent() and wipe the selection.
          if (draftEditorRef.current) {
            draftEditorRef.current.appendHtml(`<p>${trimmed}</p>`);
            return;
          }
          setter((current) => (current || '') + `<p>${trimmed}</p>`);
          return;
        }
        setter((current) => {
          const currentText = current || '';
          const separator = currentText && !/\s$/.test(currentText) ? ' ' : '';
          return (currentText + separator + trimmed).trimStart();
        });
      };

      recognition.onresult = (event) => {
        let finalPart = '';
        let interimPart = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const t = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalPart += t;
          } else {
            interimPart += t;
          }
        }
        if (finalPart) appendFinal(finalPart);
        setInterimVoiceText(interimPart.trim());
      };
      recognition.onerror = (event) => {
        console.error('Speech recognition error', event.error);
        setIsListening(false);
        setVoiceTarget(null);
        setInterimVoiceText('');
      };
      recognition.onend = () => {
        setIsListening(false);
        setVoiceTarget(null);
        setInterimVoiceText('');
      };
      recognition.start();
      setIsListening(true);
    },
    [isListening, message],
  );

  const handlePolishDraft = useCallback(() => {
    if (!selectedMessage || !draftValue.trim()) return;
    // Strip HTML tags to get raw text for the AI
    const tmp = document.createElement('div');
    tmp.innerHTML = draftValue;
    const rawText = tmp.textContent || tmp.innerText || '';
    if (!rawText.trim()) return;

    setPolishLoading(true);
    setComposerReport(null);
    const threadId = selectedMessage.id;
    clientInboxApi
      .polishDraft(threadId, { raw_text: rawText.trim(), honor_wording: keepWording })
      .then((res) => {
        // The draft box and report now belong to a different email; applying
        // this would also autosave the polished text onto the wrong thread.
        // The server keeps nothing from a polish, so say it was dropped.
        if (selectedIdRef.current !== threadId) {
          message.info('Polished draft discarded: you switched threads while it was composing.');
          return;
        }
        const data = res?.data || {};
        const polished = data.polished_html || '';
        if (polished) {
          setDraftValue(polished);
          setComposerReport(data);
          const ran = (data.tool_calls || []).length;
          message.success(
            ran ? `Draft composed after ${ran} lookup${ran === 1 ? '' : 's'}` : 'Draft polished by AI',
          );
        }
      })
      .catch((err) => {
        console.error('Failed to polish draft', err);
        message.error('Unable to polish draft with AI.');
      })
      .finally(() => {
        setPolishLoading(false);
      });
  }, [selectedMessage, draftValue, message, keepWording]);

  // ----------------------------------------------------------------- Scheduled Send
  const handleScheduleReply = useCallback(
    (scheduledAt) => {
      if (!selectedMessage) return;
      const bodyToSend = isLockedBudgetConfirmationDraft
        ? upsertBudgetNote(draftValue, budgetNote)
        : draftValue;
      if (!bodyToSend.trim() || !replyFromValue) {
        message.error('Fill in the reply body and select a sender first.');
        return;
      }
      clientInboxApi
        .scheduleReply(selectedMessage.id, {
          body: bodyToSend,
          subject: replySubject,
          to: parseAddressInput(replyToValue),
          cc: parseAddressInput(replyCcValue),
          from_email: replyFromValue,
          scheduled_at: scheduledAt,
        })
        .then((res) => {
          message.success(`Reply scheduled for ${dayjs(scheduledAt).format('ddd MMM D, h:mm A')}`);
          setDraftValue('');
          setBudgetNote('');
          setScheduleAnchor(null);
          // Refresh scheduled replies list
          loadScheduledReplies(selectedMessage.id);
        })
        .catch((err) => {
          console.error('Failed to schedule reply', err);
          message.error('Unable to schedule reply.');
        });
    },
    [selectedMessage, draftValue, budgetNote, isLockedBudgetConfirmationDraft, replyFromValue, replySubject, replyToValue, replyCcValue, message],
  );

  const loadScheduledReplies = useCallback((threadId) => {
    clientInboxApi.getScheduledReplies(threadId).then((res) => {
      setScheduledReplies(res?.data?.scheduled_replies || []);
    }).catch(() => setScheduledReplies([]));
  }, []);

  const handleCancelScheduled = useCallback(
    (srId) => {
      if (!selectedMessage) return;
      clientInboxApi.cancelScheduledReply(selectedMessage.id, srId).then(() => {
        message.success('Scheduled reply cancelled.');
        loadScheduledReplies(selectedMessage.id);
      }).catch(() => message.error('Failed to cancel.'));
    },
    [selectedMessage, message, loadScheduledReplies],
  );

  // Cancel a queued auto-send through the same scheduled-reply endpoint.
  const handleCancelAutosend = useCallback(
    (srId) => {
      const threadId = selectedMessage?.id;
      if (!threadId || !srId) return;
      setCancellingAutosend(true);
      clientInboxApi
        .cancelScheduledReply(threadId, srId)
        .then(() => {
          if (selectedIdRef.current !== threadId) return;
          message.success('Auto-send cancelled.');
          loadScheduledReplies(threadId);
        })
        .catch(() => {
          if (selectedIdRef.current === threadId) message.error('Failed to cancel the auto-send.');
        })
        .finally(() => setCancellingAutosend(false));
    },
    [selectedMessage?.id, message, loadScheduledReplies],
  );

  // "Ask the drafter" rewrote this thread's draft: reload the thread so the
  // composer shows it. Same guard as the regen poll — drop the result if the
  // thread is no longer the open one.
  const handleAgentDraftUpdated = useCallback(
    (threadId) => {
      if (!threadId || selectedIdRef.current !== threadId) return;
      clientInboxApi
        .retrieveEmailMessage(threadId)
        .then((res) => {
          if (selectedIdRef.current !== threadId || !res.data) return;
          setSelectedMessageDetail(res.data);
          setMessages((prev) =>
            prev.map((item) => (item.id === threadId ? { ...item, reply_draft: res.data.reply_draft } : item)),
          );
          message.success('Draft updated.');
        })
        .catch(() => {});
    },
    [message],
  );

  // A held draft (a regenerate was running) that staff chose to use.
  const handleAgentUseDraft = useCallback((threadId, html) => {
    if (!threadId || selectedIdRef.current !== threadId) return;
    setDraftValue(html || '');
  }, []);

  // Load scheduled replies when selecting a thread
  useEffect(() => {
    if (selectedMessage?.id) {
      loadScheduledReplies(selectedMessage.id);
    } else {
      setScheduledReplies([]);
    }
  }, [selectedMessage?.id, loadScheduledReplies]);

  // Stop speech recognition on unmount
  useEffect(() => {
    return () => {
      recognitionRef.current?.stop();
    };
  }, []);

  // Merge background-regeneration status into the list and the open thread.
  // `updates` is { [threadId]: { draft_regen_status, draft_regen_error } }.
  const applyRegenUpdates = useCallback((updates) => {
    setMessages((prev) => prev.map((item) => (updates[item.id] ? { ...item, ...updates[item.id] } : item)));
    setSelectedMessageDetail((prev) => (prev && updates[prev.id] ? { ...prev, ...updates[prev.id] } : prev));
  }, []);

  // Queues the job and returns at once: the draft is written server-side and
  // the poll below picks it up, so staff can move on to the next thread.
  const handleRegenerateDraft = useCallback(() => {
    if (!selectedMessage) {
      return;
    }
    const threadId = selectedMessage.id;
    setRegenerateLoading(true);
    const payload = { background: true };
    clientInboxApi
      .regenerateEmailThreadDraft(threadId, payload)
      .then((response) => {
        const data = response.data || {};
        const status = data.thread?.draft_regen_status || 'queued';
        applyRegenUpdates({ [threadId]: { draft_regen_status: status, draft_regen_error: '' } });
        if (data.queued) {
          message.info('Regenerating in the background. Move on; the thread shows "Draft ready" when it lands.');
        } else {
          message.info('This draft is already regenerating.');
        }
      })
      .catch((err) => {
        console.error('Failed to queue reply draft regeneration', err);
        message.error('Unable to regenerate the AI draft for this thread.');
      })
      .finally(() => {
        setRegenerateLoading(false);
      });
  }, [applyRegenUpdates, message, selectedMessage]);

  const handleBulkRegenerate = useCallback(() => {
    if (selectedThreadIds.length === 0) return;
    setBulkRegenerating(true);
    clientInboxApi
      .bulkAction({ action: 'regenerate_draft', thread_ids: selectedThreadIds })
      .then((response) => {
        const queued = response.data?.queued || [];
        const skipped = response.data?.skipped || [];
        applyRegenUpdates(
          Object.fromEntries(queued.map((id) => [id, { draft_regen_status: 'queued', draft_regen_error: '' }])),
        );
        setSelectedThreadIds([]);
        message.info(
          `Regenerating ${queued.length} draft${queued.length === 1 ? '' : 's'} in the background` +
            (skipped.length ? ` (${skipped.length} already running)` : '') +
            '.',
        );
      })
      .catch((err) => {
        console.error('Bulk regenerate failed', err);
        message.error(err?.response?.data?.detail || 'Failed to queue the selected drafts.');
      })
      .finally(() => setBulkRegenerating(false));
  }, [applyRegenUpdates, message, selectedThreadIds]);

  // Poll only the threads with a job in flight; stops when none are left.
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const regenPollKey = useMemo(() => {
    const ids = new Set(messages.filter((m) => REGEN_ACTIVE.has(m.draft_regen_status)).map((m) => m.id));
    if (selectedMessageDetail && REGEN_ACTIVE.has(selectedMessageDetail.draft_regen_status)) {
      ids.add(selectedMessageDetail.id);
    }
    return [...ids].sort((a, b) => a - b).join(',');
  }, [messages, selectedMessageDetail]);

  useEffect(() => {
    if (!regenPollKey) return undefined;
    const ids = regenPollKey.split(',').map(Number);
    const timer = setInterval(() => {
      clientInboxApi
        .getDraftRegenStatus(ids)
        .then((res) => {
          const finished = (res.data?.results || []).filter((row) => !REGEN_ACTIVE.has(row.draft_regen_status));
          if (finished.length === 0) return;
          const openId = selectedIdRef.current;
          const updates = {};
          finished.forEach((row) => {
            updates[row.id] = { draft_regen_status: row.draft_regen_status, draft_regen_error: row.draft_regen_error };
            const subject = messagesRef.current.find((m) => m.id === row.id)?.subject || 'a thread';
            if (row.draft_regen_status === 'failed') {
              message.error(`Draft failed for "${subject}".`);
            } else if (row.draft_regen_status === 'ready' && row.id !== openId) {
              message.success(`Draft ready: "${subject}".`);
            }
          });
          // List rows only. The open thread keeps its "regenerating" lock
          // until the refetch below brings the new draft; flipping it first
          // would unlock the composer on the OLD text and let autosave write
          // that back over the new draft.
          setMessages((prev) => prev.map((item) => (updates[item.id] ? { ...item, ...updates[item.id] } : item)));
          if (openId && updates[openId]) {
            // The open thread's draft was just replaced server-side: reload it
            // so the composer (and its autosave) works on the new text.
            clientInboxApi
              .retrieveEmailMessage(openId)
              .then((detailRes) => {
                if (selectedIdRef.current !== openId || !detailRes.data) return;
                setSelectedMessageDetail(detailRes.data);
                if (detailRes.data.draft_regen_status === 'ready') {
                  message.success('New draft loaded.');
                  // Seen: clear the row badge (server and list). The open
                  // detail keeps 'ready' so the effect below shows the diff.
                  clientInboxApi.markThreadRead(openId).catch(() => {});
                  setMessages((prev) =>
                    prev.map((item) => (item.id === openId ? { ...item, draft_regen_status: '' } : item)),
                  );
                }
              })
              .catch(() => {});
          }
        })
        .catch(() => {});
    }, REGEN_POLL_MS);
    return () => clearInterval(timer);
  }, [message, regenPollKey]);

  const executeStaffAction = useCallback(
    (staffAction) => {
      if (!selectedMessage || !staffAction) {
        return;
      }
      const overrides = {};
      const roleOverride = actionRoleOverrides[staffAction.dedupe_key];
      if (staffAction.allowed_overrides?.includes('role')) {
        overrides.role = roleOverride || 'admin';
      }
      setRunningActionKey(staffAction.dedupe_key);
      clientInboxApi
        .runStaffAction(selectedMessage.id, {
          action_index: staffAction.index,
          type: staffAction.type,
          overrides,
        })
        .then((response) => {
          const payload = response.data || {};
          const updatedThread = payload.thread;
          if (updatedThread) {
            setMessages((prev) =>
              prev.map((item) =>
                item.id === updatedThread.id ? { ...item, ...updatedThread } : item,
              ),
            );
            setSelectedMessageDetail(updatedThread);
            // The re-draft is the whole point — pull it into the composer so
            // the reply reads "done", not "we'll get that set up".
            setDraftValue(updatedThread.reply_draft || '');
          }
          message.success(payload.execution?.summary || 'Action completed.');
          if (payload.redraft_error) {
            message.warning(
              'Action completed, but the draft could not be regenerated — edit the reply by hand.',
            );
          }
        })
        .catch((err) => {
          console.error('Failed to run staff action', err);
          message.error(
            err?.response?.data?.detail || 'Could not complete that action.',
          );
        })
        .finally(() => {
          setRunningActionKey('');
        });
    },
    [actionRoleOverrides, message, selectedMessage],
  );

  const handleRunStaffAction = useCallback(
    (staffAction) => {
      if (!staffAction) {
        return;
      }
      // Anything that emails a real person gets a confirmation step — an
      // invite cannot be unsent.
      if (staffAction.risk === 'confirm') {
        setPendingStaffAction(staffAction);
        return;
      }
      executeStaffAction(staffAction);
    },
    [executeStaffAction],
  );

  const handleRefresh = useCallback(() => {
    setLoading(true);
    clientInboxApi.triggerIngest()
      .then((res) => {
        const count = res?.data?.ingested || 0;
        if (count > 0) {
          message.success(`${count} new email${count !== 1 ? 's' : ''} ingested.`);
        }
      })
      .catch(() => {})
      .finally(() => {
        loadMessages();
        loadDashboardSummary();
      });
  }, [loadDashboardSummary, loadMessages, message]);

  const handleSaveDraft = useCallback(() => {
    if (!selectedMessage) {
      return;
    }
    const draftToSave = isLockedBudgetConfirmationDraft
      ? upsertBudgetNote(draftValue, budgetNote)
      : draftValue;
    setSavingDraft(true);
    clientInboxApi
      .updateEmailMessage(selectedMessage.id, { reply_draft: draftToSave })
      .then((response) => {
        const updated = response.data;
        setMessages((prev) =>
          prev.map((item) => (item.id === updated.id ? { ...item, ...updated } : item)),
        );
        setSelectedMessageDetail(updated);
        if (isLockedBudgetConfirmationDraft) {
          setDraftValue(draftToSave);
          setBudgetNote('');
        }
        message.success('Draft saved.');
      })
      .catch((err) => {
        console.error('Failed to save reply draft', err);
        message.error('Unable to save the reply draft.');
      })
      .finally(() => {
        setSavingDraft(false);
      });
  }, [budgetNote, draftValue, isLockedBudgetConfirmationDraft, message, selectedMessage]);

  const handleCreateTask = useCallback(() => {
    if (!selectedMessage || selectedMessage.created_task) {
      return;
    }
    setCreatingTask(true);
    const payload = {
      client: selectedMessage.client,
      title: selectedMessage.subject || 'Client email follow-up',
      notes: selectedMessage.summary || '',
      description: selectedMessage.body_text || '',
      status: 'backlog',
      priority: 'medium',
    };

    createTask(payload)
      .then((taskResponse) => {
        const task = taskResponse.data;
        return clientInboxApi
          .updateEmailMessage(selectedMessage.id, {
            created_task: task.id,
            processing_status: 'task_created',
          })
          .then((emailResponse) => {
            const updated = emailResponse.data;
            setMessages((prev) =>
              prev.map((item) => (item.id === updated.id ? { ...item, ...updated } : item)),
            );
            setSelectedMessageDetail(updated);
            message.success('Task created and linked to the email.');
          });
      })
      .catch((err) => {
        console.error('Failed to create a task for the email', err);
        message.error('Unable to create a task for this email.');
      })
      .finally(() => {
        setCreatingTask(false);
      });
  }, [message, selectedMessage]);

  const handleSendReply = useCallback(() => {
    if (!selectedMessage) {
      return;
    }
    if (!replyFromValue) {
      message.error('Select a sender before sending.');
      return;
    }
    if (sendBlocked) {
      message.error('This thread is currently marked as not ready to send from the AI workflow.');
      return;
    }
    const bodyToSend = isLockedBudgetConfirmationDraft
      ? upsertBudgetNote(draftValue, budgetNote)
      : draftValue;
    const trimmedBody = bodyToSend.trim();
    if (!trimmedBody) {
      message.error('Reply body cannot be empty.');
      return;
    }
    let payload;
    if (replyAttachments.length > 0) {
      const formData = new FormData();
      formData.append('body', bodyToSend);
      formData.append('subject', replySubject);
      formData.append('from_email', replyFromValue);
      parseAddressInput(replyToValue).forEach((addr) => formData.append('to', addr));
      parseAddressInput(replyCcValue).forEach((addr) => formData.append('cc', addr));
      replyAttachments.forEach((file) => formData.append('attachments', file));
      payload = formData;
    } else {
      payload = {
        body: bodyToSend,
        subject: replySubject,
        to: parseAddressInput(replyToValue),
        cc: parseAddressInput(replyCcValue),
        from_email: replyFromValue,
      };
    }
    // Undo send: queue the send with a 5-second delay
    const threadId = selectedMessage.id;
    setUndoSendData({ threadId, payload });
    setSendingReply(true);

    const timer = setTimeout(() => {
      clientInboxApi
        .replyToEmailMessage(threadId, payload)
        .then((response) => {
          try {
            const updated = response.data;
            // Update the detail pane with full response data
            setSelectedMessageDetail(updated);
            // Update only list-relevant fields in the messages array
            setMessages((prev) =>
              prev.map((item) =>
                item.id === updated.id
                  ? {
                      ...item,
                      conversation_status: updated.conversation_status,
                      reply_draft: updated.reply_draft,
                      processing_status: updated.processing_status,
                      message_count: updated.message_count,
                      last_received_at: updated.last_received_at,
                      is_read: true,
                    }
                  : item,
              ),
            );
            // Navigate to next needs_response thread after state settles
            setTimeout(() => {
              setMessages((current) => {
                const currentIndex = current.findIndex((item) => item.id === updated.id);
                const nextThread = current.find(
                  (item, idx) => idx !== currentIndex && item.conversation_status === 'needs_response',
                );
                if (nextThread) {
                  setSelectedId(nextThread.id);
                  setSearchParams({ thread: String(nextThread.id) });
                }
                return current; // don't mutate
              });
            }, 100);
            setDraftValue('');
            setBudgetNote('');
            setReplyAttachments([]);
            message.success('Reply sent.');
            loadDashboardSummary();
          } catch (innerErr) {
            console.error('Reply success handler crashed:', innerErr);
            message.error('Reply sent but UI failed to update. Please refresh.');
          }
        })
        .catch((err) => {
          console.error('Failed to send reply', err);
          message.error('Unable to send the reply.');
        })
        .finally(() => {
          setSendingReply(false);
          setUndoSendData(null);
        });
    }, 5000);

    // Store the timer so undo can cancel it
    setUndoSendData((prev) => ({ ...prev, timer }));
  }, [
    budgetNote,
    draftValue,
    isLockedBudgetConfirmationDraft,
    loadDashboardSummary,
    message,
    replyCcValue,
    replyFromValue,
    replySubject,
    replyToValue,
    sendBlocked,
    selectedMessage,
    setSearchParams,
  ]);

  // The draft reads as if suggested actions are done; stop a send while an
  // executable one hasn't been run.
  const handleSendClick = useCallback(() => {
    const pending = findUnrunStaffAction(selectedMessage);
    if (pending) {
      setUnrunSendAction(pending);
      return;
    }
    handleSendReply();
  }, [handleSendReply, selectedMessage]);

  // A thread switch drops the prompt.
  useEffect(() => {
    setUnrunSendAction(null);
  }, [selectedId]);

  const totalPages = useMemo(() => {
    if (!totalCount || !pageSize) {
      return 1;
    }
    return Math.max(1, Math.ceil(totalCount / pageSize));
  }, [pageSize, totalCount]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  return (
    <Box p={{ xs: 2, md: 3 }}>
      <Stack spacing={3}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
          <Stack direction="row" alignItems="center" spacing={1}>
            {isMobile && mobilePanel === 'detail' ? (
              <IconButton
                onClick={() => {
                  setMobilePanel('queue');
                }}
                size="small"
              >
                <ArrowBackIcon />
              </IconButton>
            ) : null}
            <Typography variant={isMobile ? 'h5' : 'h4'}>Client Inbox</Typography>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              size={isMobile ? 'small' : 'medium'}
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              disabled={loading}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              size={isMobile ? 'small' : 'medium'}
              onClick={() => setComposeOpen(true)}
            >
              New Email
            </Button>
            <Button
              variant="outlined"
              size={isMobile ? 'small' : 'medium'}
              onClick={() => setAutonomyOpen(true)}
            >
              Autonomy
            </Button>
            {!isMobile ? (
              <Button
                variant="outlined"
                component={RouterLink}
                to="/tasks"
                startIcon={<TaskIcon />}
              >
                Task Board
              </Button>
            ) : null}
          </Stack>
        </Stack>



        {!(isMobile && mobilePanel === 'detail') ? (
          <Box
            sx={{
              position: { xs: 'sticky', lg: 'static' },
              top: 'var(--MainNav-height, 56px)',
              zIndex: 5,
              bgcolor: 'background.default',
              pt: { xs: 1, lg: 0 },
              mx: { xs: -2, md: -3 },
              px: { xs: 2, md: 3 },
            }}
          >
            <Stack spacing={1.25}>
              {/* Metric-only stats (separated from the status filters) */}
              {dashboardSummary ? (
                <Stack
                  direction="row"
                  spacing={2.5}
                  flexWrap="wrap"
                  useFlexGap
                  alignItems="baseline"
                  sx={{ px: 0.5 }}
                >
                  <Stack direction="row" spacing={0.5} alignItems="baseline">
                    <Typography variant="caption" color="text.secondary">Closed today</Typography>
                    <Typography variant="subtitle2" fontWeight={700}>
                      {dashboardSummary.closed_today ?? 0}
                    </Typography>
                  </Stack>
                  {dashboardSummary.auto_closed_today > 0 ? (
                    <Stack direction="row" spacing={0.5} alignItems="baseline">
                      <Typography variant="caption" color="text.secondary">Auto-closed today</Typography>
                      <Typography variant="subtitle2" fontWeight={700}>
                        {dashboardSummary.auto_closed_today}
                      </Typography>
                    </Stack>
                  ) : null}
                </Stack>
              ) : null}

              {/* Status filter pills — scroll horizontally on mobile, wrap on desktop */}
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  overflowX: 'auto',
                  flexWrap: { xs: 'nowrap', lg: 'wrap' },
                  pb: 0.5,
                  '&::-webkit-scrollbar': { display: 'none' },
                  scrollbarWidth: 'none',
                }}
              >
                {STATUS_FILTER_PILLS.map((pill) => {
                  const selected =
                    pill.value === 'all'
                      ? filters.conversation_status === 'all'
                      : filters.conversation_status === pill.value;
                  const count = pill.countKey && dashboardSummary ? dashboardSummary[pill.countKey] : null;
                  const label = count > 0 ? `${pill.label} · ${count}` : pill.label;
                  return (
                    <Chip
                      key={pill.value}
                      label={label}
                      color={selected ? pill.color : 'default'}
                      variant={selected ? 'filled' : 'outlined'}
                      onClick={() =>
                        handleFilterChange(
                          'conversation_status',
                          pill.value === 'all' || filters.conversation_status === pill.value
                            ? 'all'
                            : pill.value,
                        )
                      }
                      sx={{ flexShrink: 0, fontWeight: selected ? 700 : 500 }}
                    />
                  );
                })}
              </Box>

              {/* Mobile: search + filters button (secondary filters live in the drawer) */}
              {isMobile ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField
                    value={searchInput}
                    onChange={(event) => setSearchInput(event.target.value)}
                    placeholder="Search subject or summary"
                    size="small"
                    sx={{ flex: 1 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Badge badgeContent={secondaryFilterCount} color="primary">
                    <IconButton
                      onClick={() => setFiltersDrawerOpen(true)}
                      sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}
                    >
                      <TuneIcon />
                    </IconButton>
                  </Badge>
                </Stack>
              ) : null}
            </Stack>
          </Box>
        ) : null}

        <Card sx={{ display: { xs: 'none', lg: 'block' } }}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
              <TextField
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                placeholder="Search subject or summary"
                size="small"
                sx={{ minWidth: 220, flex: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <Autocomplete
                size="small"
                sx={{ minWidth: 220 }}
                options={clientOptions}
                loading={clientsLoading}
                value={selectedClientOption}
                onChange={(_, option) => handleFilterChange('client', option ? String(option.value) : null)}
                onInputChange={handleClientSearch}
                getOptionLabel={(option) => option.label || ''}
                isOptionEqualToValue={(option, value) => String(option.value) === String(value.value)}
                renderInput={(params) => (
                  <TextField {...params} label="Client" placeholder="Search clients..." />
                )}
              />
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Ownership</InputLabel>
                <Select
                  label="Ownership"
                  value={filters.owner}
                  onChange={(event) => handleFilterChange('owner', event.target.value)}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="me">Mine</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Task</InputLabel>
                <Select
                  label="Task"
                  value={filters.task}
                  onChange={(event) => handleFilterChange('task', event.target.value)}
                >
                  {TASK_FILTER_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Processing</InputLabel>
                <Select
                  label="Processing"
                  value={filters.status}
                  onChange={(event) => handleFilterChange('status', event.target.value)}
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {filters.conversation_status !== 'all' || filters.client || filters.owner !== 'all' || filters.task !== 'all' || filters.status !== 'all' || filters.search ? (
                <Button
                  size="small"
                  onClick={() => {
                    setFilters(getInitialFilters());
                    setSearchInput('');
                  }}
                >
                  Clear filters
                </Button>
              ) : null}
            </Stack>
          </CardContent>
        </Card>

        {/* Mobile secondary-filters drawer (Client / Ownership / Task / Processing) */}
        <Drawer
          anchor="bottom"
          open={filtersDrawerOpen}
          onClose={() => setFiltersDrawerOpen(false)}
          PaperProps={{
            sx: { borderTopLeftRadius: 16, borderTopRightRadius: 16, maxHeight: '85vh', p: 2 },
          }}
        >
          <Stack spacing={2}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="h6">Filters</Typography>
              <Button
                size="small"
                onClick={() => {
                  setFilters(getInitialFilters());
                  setSearchInput('');
                }}
              >
                Clear
              </Button>
            </Stack>
            <Autocomplete
              size="small"
              fullWidth
              options={clientOptions}
              loading={clientsLoading}
              value={selectedClientOption}
              onChange={(_, option) => handleFilterChange('client', option ? String(option.value) : null)}
              onInputChange={handleClientSearch}
              getOptionLabel={(option) => option.label || ''}
              isOptionEqualToValue={(option, value) => String(option.value) === String(value.value)}
              renderInput={(params) => (
                <TextField {...params} label="Client" placeholder="Search clients..." />
              )}
            />
            <FormControl size="small" fullWidth>
              <InputLabel>Ownership</InputLabel>
              <Select
                label="Ownership"
                value={filters.owner}
                onChange={(event) => handleFilterChange('owner', event.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="me">Mine</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel>Task</InputLabel>
              <Select
                label="Task"
                value={filters.task}
                onChange={(event) => handleFilterChange('task', event.target.value)}
              >
                {TASK_FILTER_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel>Processing status</InputLabel>
              <Select
                label="Processing status"
                value={filters.status}
                onChange={(event) => handleFilterChange('status', event.target.value)}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button variant="contained" fullWidth onClick={() => setFiltersDrawerOpen(false)}>
              Done
            </Button>
          </Stack>
        </Drawer>

        {error ? <Alert severity="error">{error}</Alert> : null}

        <Grid container spacing={3}>
          <Grid item xs={12} lg={3} sx={{ display: isMobile && mobilePanel !== 'queue' ? 'none' : 'block' }}>
            <Card sx={{ height: '100%', borderRadius: 3 }}>
              <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                <Box px={2.5} py={2} borderBottom="1px solid" borderColor="divider">
                  <Typography variant="overline" color="text.secondary">
                    Queue
                  </Typography>
                  <Typography variant="h6">Client Inbox</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {totalCount.toLocaleString()} email{totalCount === 1 ? '' : 's'} across current filters
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    Sorted by priority (urgency + budget + status + wait time)
                  </Typography>
                </Box>
                {messages.length > 0 && (
                  <Box
                    px={1.5}
                    py={0.75}
                    display="flex"
                    alignItems="center"
                    gap={1}
                    borderBottom="1px solid"
                    borderColor="divider"
                    bgcolor={selectedThreadIds.length > 0 ? 'action.selected' : 'transparent'}
                  >
                    <Checkbox
                      size="small"
                      checked={selectedThreadIds.length === messages.length && messages.length > 0}
                      indeterminate={selectedThreadIds.length > 0 && selectedThreadIds.length < messages.length}
                      onChange={handleSelectAllThreads}
                    />
                    {selectedThreadIds.length > 0 ? (
                      <>
                        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                          {selectedThreadIds.length} selected
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<CloseIcon />}
                          disabled={bulkClosing}
                          onClick={handleBulkClose}
                        >
                          {bulkClosing ? 'Closing...' : 'Close'}
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<AutoAwesomeIcon />}
                          disabled={bulkRegenerating}
                          onClick={handleBulkRegenerate}
                        >
                          {bulkRegenerating ? 'Queuing…' : 'Regenerate drafts'}
                        </Button>
                      </>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        Select all
                      </Typography>
                    )}
                  </Box>
                )}
                {loading ? (
                  <Box display="flex" justifyContent="center" alignItems="center" py={6}>
                    <CircularProgress size={32} />
                  </Box>
                ) : messages.length === 0 ? (
                  <Box px={3} py={6} textAlign="center">
                    <Typography variant="body2" color="text.secondary">
                      No emails match the current filters.
                    </Typography>
                  </Box>
                ) : (
                  <List disablePadding sx={{ maxHeight: isMobile ? 'calc(100dvh - 250px)' : '72vh', overflow: 'auto' }}>
                    {messages.map((item) => {
                      const addresses = normalizeAddresses(item.to_addresses || []);
                      const initials = item.from_email ? item.from_email.charAt(0).toUpperCase() : '?';
                      const workMeta = WORK_DECISION_META[item.work_decision] || null;
                      const isUnread = item.is_read === false;
                      const convColor = CONVERSATION_STATUS_COLOR_MAP[item.conversation_status] || 'default';
                      const convLabel = CONVERSATION_STATUS_LABEL_MAP[item.conversation_status] || item.conversation_status;
                      const budgetTier = item.client_budget >= 100000 ? '$$$$' : item.client_budget >= 30000 ? '$$$' : item.client_budget >= 15000 ? '$$' : item.client_budget > 0 ? '$' : '';
                      const urgency = item.urgency_score || 0;
                      const urgencyLabel = urgency >= 75 ? 'Critical' : urgency >= 50 ? 'High' : urgency >= 25 ? 'Medium' : urgency > 0 ? 'Low' : '';
                      const urgencyColor = urgency >= 75 ? 'error' : urgency >= 50 ? 'warning' : urgency >= 25 ? 'info' : 'default';
                      // Urgency coloring for time-since-received
                      const hoursAgo = item.received_at ? dayjs().diff(dayjs(item.received_at), 'hour') : 0;
                      const timeColor = hoursAgo > 24 ? 'error.main' : hoursAgo > 8 ? 'warning.main' : hoursAgo > 2 ? 'text.secondary' : 'success.main';
                      const timeAgo = item.received_at ? dayjs(item.received_at).fromNow() : '';
                      return (
                        <ListItemButton
                          key={item.id}
                          onClick={() => {
                            setSelectedId(item.id);
                            if (isMobile) {
                              setMobilePanel('detail');
                            }
                          }}
                          selected={item.id === selectedId}
                          alignItems="flex-start"
                          sx={{
                            borderLeft: '4px solid',
                            borderLeftColor:
                              item.conversation_status === 'needs_client_assignment'
                                ? 'warning.main'
                                : item.conversation_status === 'needs_response'
                                ? 'error.main'
                                : item.conversation_status === 'in_progress'
                                ? 'info.main'
                                : item.conversation_status === 'awaiting_client'
                                ? 'warning.main'
                                : item.conversation_status === 'closed'
                                ? 'grey.400'
                                : 'transparent',
                            px: 2,
                            py: 1.5,
                            bgcolor: isUnread ? 'action.hover' : 'inherit',
                          }}
                        >
                          <Checkbox
                            size="small"
                            checked={selectedThreadIds.includes(item.id)}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleThread(item.id);
                            }}
                            sx={{ mr: 0.5, mt: 0.5 }}
                          />
                          <ListItemAvatar>
                            <Avatar>{initials}</Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Stack spacing={0.75}>
                                <Typography variant="subtitle2" noWrap fontWeight={isUnread ? 700 : 500}>
                                  {item.subject || 'Untitled email'}
                                </Typography>
                                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                                  {item.conversation_status ? (
                                    <Chip
                                      size="small"
                                      label={convLabel}
                                      color={convColor}
                                    />
                                  ) : null}
                                  {REGEN_CHIP[item.draft_regen_status] ? (
                                    <Tooltip
                                      title={item.draft_regen_status === 'failed' ? item.draft_regen_error || 'Regeneration failed' : ''}
                                      arrow
                                    >
                                      <Chip
                                        size="small"
                                        icon={REGEN_ACTIVE.has(item.draft_regen_status) ? <CircularProgress size={10} /> : undefined}
                                        {...REGEN_CHIP[item.draft_regen_status]}
                                      />
                                    </Tooltip>
                                  ) : null}
                                  {item.work_decision ? (
                                    <Chip
                                      size="small"
                                      variant="outlined"
                                      label={formatWorkDecisionLabel(item.work_decision) || item.work_decision}
                                    />
                                  ) : null}
                                  {urgencyLabel ? (
                                    <Tooltip title={item.urgency_reason || `Urgency: ${urgency}/100`} arrow>
                                      <Chip
                                        size="small"
                                        color={urgencyColor}
                                        variant={urgency < 25 ? 'outlined' : 'filled'}
                                        label={`${urgencyLabel} (${urgency})`}
                                      />
                                    </Tooltip>
                                  ) : null}
                                  {item.is_follow_up ? (
                                    <Chip size="small" color="warning" icon={<AccessAlarmIcon />} label="Follow-up" />
                                  ) : item.follow_up_at ? (
                                    <Tooltip title={`Follow-up: ${dayjs(item.follow_up_at).format('MMM D, h:mm A')}`} arrow>
                                      <Chip size="small" variant="outlined" color="info" icon={<AccessAlarmIcon />} label={dayjs(item.follow_up_at).format('MMM D')} />
                                    </Tooltip>
                                  ) : null}
                                  {item.note_count > 0 ? (
                                    <Chip size="small" variant="outlined" label={`${item.note_count} note${item.note_count > 1 ? 's' : ''}`} />
                                  ) : null}
                                  {item.is_parent_level ? (
                                    <Chip
                                      size="small"
                                      color="warning"
                                      variant="outlined"
                                      label={`Parent: ${item.parent_company_name || 'Unassigned'}`}
                                    />
                                  ) : null}
                                  {item.is_parent_level && item.suggested_child_client?.client_name ? (
                                    <Tooltip title={item.suggested_child_client.reason || 'AI suggestion'} arrow>
                                      <Chip
                                        size="small"
                                        color="info"
                                        variant="outlined"
                                        label={`Suggested: ${item.suggested_child_client.client_name}`}
                                      />
                                    </Tooltip>
                                  ) : null}
                                </Stack>
                              </Stack>
                            }
                            secondary={
                              <Stack spacing={0.5} mt={0.75}>
                                <Typography variant="caption" color="text.secondary" noWrap>
                                  {item.client_name || item.parent_company_name || 'Unassigned'}{budgetTier ? ` ${budgetTier}` : ''} &middot; From {item.from_email}
                                </Typography>
                                {addresses.length > 0 ? (
                                  <Typography variant="caption" color="text.secondary" noWrap>
                                    To {addresses.join(', ')}
                                  </Typography>
                                ) : null}
                                <Typography variant="body2" color="text.secondary" noWrap>
                                  {item.summary || item.body_preview || 'No summary available'}
                                </Typography>
                                {item.assigned_owner_name || item.assigned_owner_email ? (
                                  <Typography variant="caption" color="text.secondary" noWrap>
                                    Owner: {item.assigned_owner_name || item.assigned_owner_email}
                                  </Typography>
                                ) : null}
                                <Typography variant="caption" sx={{ color: timeColor }}>
                                  {timeAgo || 'Unknown'}
                                </Typography>
                              </Stack>
                            }
                          />
                        </ListItemButton>
                      );
                    })}
                  </List>
                )}
              </CardContent>
              {totalPages > 1 ? (
                <Box px={2} py={2} display="flex" justifyContent="center">
                  <Pagination
                    color="primary"
                    count={totalPages}
                    page={Math.min(page, totalPages)}
                    onChange={(_, value) => setPage(value)}
                  />
                </Box>
              ) : null}
            </Card>
          </Grid>

          <Grid
            item
            xs={12}
            lg={9}
            sx={{ display: isMobile && mobilePanel !== 'detail' ? 'none' : 'block' }}
          >
            <Card sx={{ minHeight: isMobile ? 'auto' : 480, borderRadius: 3 }}>
              <CardContent sx={{ px: isMobile ? 1.5 : 3 }}>
                {!selectedMessage ? (
                  <Box py={10} textAlign="center">
                    <Typography variant="body1" color="text.secondary">
                      Select an email from the queue to see the thread and the draft.
                    </Typography>
                  </Box>
                ) : detailLoading && !selectedMessageDetail ? (
                  <Box display="flex" justifyContent="center" alignItems="center" py={10}>
                    <CircularProgress size={32} />
                  </Box>
                ) : (
                  <Stack spacing={3}>
                    <Box
                      sx={{
                        p: 2.5,
                        borderRadius: 3,
                        background:
                          'linear-gradient(135deg, rgba(15,23,42,0.04) 0%, rgba(59,130,246,0.10) 100%)',
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Stack spacing={1}>
                        <Typography variant="overline" color="text.secondary">
                          Work Area
                        </Typography>
                        <Typography variant="h5">{selectedMessage.subject || 'Untitled email'}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          From {selectedMessage.from_email}
                        </Typography>
                        {normalizeAddresses(selectedMessage.to_addresses).length > 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            To: {normalizeAddresses(selectedMessage.to_addresses).join(', ')}
                          </Typography>
                        ) : null}
                        {normalizeAddresses(selectedMessage.cc_addresses).length > 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            CC: {normalizeAddresses(selectedMessage.cc_addresses).join(', ')}
                          </Typography>
                        ) : null}
                        <Typography variant="body2" color="text.secondary">
                          Received{' '}
                          {selectedMessage.received_at
                            ? dayjs(selectedMessage.received_at).format('MMM D, YYYY h:mm A')
                            : 'Unknown'}
                        </Typography>
                        {selectedMessage.is_follow_up && (
                          <Alert severity="warning" sx={{ py: 0, mb: 0.5 }}>
                            Follow-up reminder triggered — client has not replied.
                            {selectedMessage.follow_up_note ? ` Note: ${selectedMessage.follow_up_note}` : ''}
                          </Alert>
                        )}
                        {!selectedMessage.is_follow_up && selectedMessage.follow_up_at && (
                          <Alert severity="info" sx={{ py: 0, mb: 0.5 }}>
                            Follow-up set for {dayjs(selectedMessage.follow_up_at).format('ddd MMM D, h:mm A')}
                          </Alert>
                        )}
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          {workDecisionMeta ? (
                            <Chip
                              label={workDecisionMeta.label}
                              size="small"
                              color={workDecisionMeta.color}
                            />
                          ) : null}
                          {selectedMessage.created_task_title ? (
                            <Chip
                              label={`Task: ${selectedMessage.created_task_title}`}
                              size="small"
                              color="primary"
                            />
                          ) : null}
                          {selectedMessage.assigned_owner_name || selectedMessage.assigned_owner_email ? (
                            <Chip
                              label={`Owner: ${
                                selectedMessage.assigned_owner_name || selectedMessage.assigned_owner_email
                              }`}
                              size="small"
                              variant="outlined"
                            />
                          ) : null}
                        </Stack>
                      </Stack>
                    </Box>

                    {selectedMessage.is_parent_level ? (
                      <ParentLevelAssignBanner
                        thread={selectedMessage}
                        clients={clients}
                        onAssigned={(updated) => {
                          setSelectedMessageDetail(updated);
                          setMessages((prev) =>
                            prev.map((m) => (m.id === updated.id ? { ...m, ...updated } : m)),
                          );
                          loadDashboardSummary();
                        }}
                        onError={(err) => {
                          const detail = err?.response?.data?.detail
                            || err?.response?.data?.client_id?.[0]
                            || 'Unable to assign client.';
                          message.error(detail);
                        }}
                      />
                    ) : null}

                    {/* Quick Actions */}
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {selectedMessage.conversation_status !== 'closed' ? (
                        <Button
                          size="small"
                          variant="outlined"
                          color="success"
                          onClick={() => {
                            clientInboxApi.updateConversationStatus(selectedMessage.id, { conversation_status: 'closed' })
                              .then((res) => {
                                setSelectedMessageDetail(res.data);
                                setMessages((prev) => prev.map((m) => m.id === res.data.id ? { ...m, conversation_status: 'closed' } : m));
                                loadDashboardSummary();
                              });
                          }}
                        >
                          Close
                        </Button>
                      ) : (
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          onClick={() => {
                            clientInboxApi.updateConversationStatus(selectedMessage.id, { conversation_status: 'needs_response' })
                              .then((res) => {
                                setSelectedMessageDetail(res.data);
                                setMessages((prev) => prev.map((m) => m.id === res.data.id ? { ...m, conversation_status: 'needs_response' } : m));
                                loadDashboardSummary();
                              });
                          }}
                        >
                          Reopen
                        </Button>
                      )}
                      {selectedMessage.conversation_status !== 'in_progress' ? (
                        <Button
                          size="small"
                          variant="outlined"
                          color="info"
                          onClick={() => {
                            clientInboxApi.updateConversationStatus(selectedMessage.id, { conversation_status: 'in_progress' })
                              .then((res) => {
                                setSelectedMessageDetail(res.data);
                                setMessages((prev) => prev.map((m) => m.id === res.data.id ? { ...m, conversation_status: 'in_progress' } : m));
                                loadDashboardSummary();
                              });
                          }}
                        >
                          In Progress
                        </Button>
                      ) : null}
                      <FormControl size="small" sx={{ minWidth: 130 }}>
                        <Select
                          displayEmpty
                          value=""
                          renderValue={() => 'Snooze'}
                          size="small"
                          sx={{ fontSize: 13, height: 31 }}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (!val) return;
                            let snoozeUntil;
                            if (val === '1h') snoozeUntil = dayjs().add(1, 'hour');
                            else if (val === '4h') snoozeUntil = dayjs().add(4, 'hour');
                            else if (val === 'tomorrow') snoozeUntil = dayjs().add(1, 'day').hour(9).minute(0).second(0);
                            else if (val === 'friday') {
                              const fri = dayjs().day(5);
                              snoozeUntil = (fri.isBefore(dayjs()) ? fri.add(1, 'week') : fri).hour(9).minute(0).second(0);
                            } else if (val === 'monday') {
                              const mon = dayjs().day(1);
                              snoozeUntil = (mon.isBefore(dayjs()) || mon.isSame(dayjs(), 'day') ? mon.add(1, 'week') : mon).hour(9).minute(0).second(0);
                            }
                            if (!snoozeUntil) return;
                            clientInboxApi.updateConversationStatus(selectedMessage.id, {
                              conversation_status: 'snoozed',
                              snoozed_until: snoozeUntil.toISOString(),
                            })
                              .then((res) => {
                                setSelectedMessageDetail(res.data);
                                setMessages((prev) => prev.map((m) => m.id === res.data.id ? { ...m, conversation_status: 'snoozed' } : m));
                                loadDashboardSummary();
                                message.success(`Snoozed until ${snoozeUntil.format('ddd h:mm A')}`);
                              });
                          }}
                        >
                          <MenuItem value="1h">1 hour</MenuItem>
                          <MenuItem value="4h">4 hours</MenuItem>
                          <MenuItem value="tomorrow">Tomorrow 9am</MenuItem>
                          <MenuItem value="friday">Friday 9am</MenuItem>
                          <MenuItem value="monday">Monday 9am</MenuItem>
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ minWidth: 150 }}>
                        <Select
                          displayEmpty
                          value=""
                          renderValue={() => (
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <AccessAlarmIcon sx={{ fontSize: 14 }} />
                              <span>Follow-up</span>
                            </Stack>
                          )}
                          size="small"
                          sx={{ fontSize: 13, height: 31 }}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (!val) return;
                            let followUpAt;
                            if (val === '3d') followUpAt = dayjs().add(3, 'day').hour(9).minute(0).second(0);
                            else if (val === '5d') followUpAt = dayjs().add(5, 'day').hour(9).minute(0).second(0);
                            else if (val === '1w') followUpAt = dayjs().add(1, 'week').hour(9).minute(0).second(0);
                            else if (val === '2w') followUpAt = dayjs().add(2, 'week').hour(9).minute(0).second(0);
                            if (!followUpAt) return;
                            clientInboxApi.updateConversationStatus(selectedMessage.id, {
                              conversation_status: 'awaiting_client',
                              follow_up_at: followUpAt.toISOString(),
                              follow_up_note: '',
                            })
                              .then((res) => {
                                setSelectedMessageDetail(res.data);
                                setMessages((prev) => prev.map((m) => m.id === res.data.id ? { ...m, conversation_status: 'awaiting_client', follow_up_at: res.data.follow_up_at } : m));
                                loadDashboardSummary();
                                message.success(`Follow-up set for ${followUpAt.format('ddd MMM D')}`);
                              });
                          }}
                        >
                          <MenuItem value="3d">3 days</MenuItem>
                          <MenuItem value="5d">5 days</MenuItem>
                          <MenuItem value="1w">1 week</MenuItem>
                          <MenuItem value="2w">2 weeks</MenuItem>
                        </Select>
                      </FormControl>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setForwardOpen(true)}
                      >
                        Forward
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          clientInboxApi.claimThread(selectedMessage.id)
                            .then((res) => {
                              const updated = res.data;
                              setSelectedMessageDetail(updated);
                              setMessages((prev) => prev.map((m) => m.id === updated.id ? { ...m, assigned_owner_name: updated.assigned_owner_name, assigned_owner_email: updated.assigned_owner_email } : m));
                              message.success('Claimed.');
                            });
                        }}
                      >
                        Claim
                      </Button>
                      <Button
                        size="small"
                        variant={createTaskRecommended && !selectedMessage.created_task ? 'contained' : 'outlined'}
                        color="secondary"
                        startIcon={<TaskIcon />}
                        onClick={handleCreateTask}
                        disabled={Boolean(selectedMessage.created_task) || creatingTask}
                      >
                        {selectedMessage.created_task
                          ? 'Task linked'
                          : creatingTask
                          ? 'Creating…'
                          : createTaskRecommended
                          ? 'Create recommended task'
                          : 'Create task'}
                      </Button>
                      {(selectedMessage.relevant_links || []).map((link) => (
                        <Button
                          key={link.url}
                          size="small"
                          variant="text"
                          href={link.url}
                          target="_blank"
                          endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                          sx={{ textTransform: 'none' }}
                        >
                          {link.label}
                        </Button>
                      ))}
                      {selectedMessage.conversation_status ? (
                        <Chip
                          label={CONVERSATION_STATUS_LABEL_MAP[selectedMessage.conversation_status] || selectedMessage.conversation_status}
                          color={CONVERSATION_STATUS_COLOR_MAP[selectedMessage.conversation_status] || 'default'}
                          size="small"
                        />
                      ) : null}
                    </Stack>

                    {workDecisionMeta ? (
                      <Alert severity={workDecisionMeta.severity}>
                        {workDecisionMeta.message}
                        {selectedQuestionRouter?.reason ? ` ${selectedQuestionRouter.reason}` : ''}
                      </Alert>
                    ) : null}

                    <AutosendBanner
                      payload={selectedAnalysisPayload}
                      pendingIds={scheduledReplies.map((sr) => sr.id)}
                      cancelling={cancellingAutosend}
                      onCancel={handleCancelAutosend}
                    />
                    <InvestigationBanner
                      payload={selectedAnalysisPayload}
                      onInvestigate={() => agentPanelRef.current?.investigate()}
                    />

                    {Array.isArray(selectedMessage.suggested_actions) && selectedMessage.suggested_actions.length > 0 ? (
                      <Card variant="outlined" sx={{ borderRadius: 2 }}>
                        <CardContent>
                          <Stack spacing={1.5}>
                            <Typography variant="subtitle1">Suggested Actions</Typography>
                            {selectedMessage.suggested_actions.map((action, index) => {
                              const staffAction = (selectedMessage.staff_actions || []).find(
                                (entry) => entry.index === index,
                              );
                              const roleKey = staffAction?.dedupe_key || '';
                              return (
                                <SuggestedActionCard
                                  key={`action-${index}`}
                                  action={action}
                                  index={index}
                                  staffAction={staffAction}
                                  running={Boolean(roleKey) && runningActionKey === roleKey}
                                  roleValue={actionRoleOverrides[roleKey] || 'admin'}
                                  onRoleChange={(value) =>
                                    setActionRoleOverrides((prev) => ({ ...prev, [roleKey]: value }))
                                  }
                                  onRun={handleRunStaffAction}
                                />
                              );
                            })}
                          </Stack>
                        </CardContent>
                      </Card>
                    ) : null}

                    {latestThreadMessage ? (
                      <Card variant="outlined" sx={{ borderRadius: 2 }}>
                        <CardContent>
                          <Typography variant="subtitle1" mb={1.5}>Latest message</Typography>
                          <ThreadMessage
                            key={latestThreadMessage.id}
                            msg={latestThreadMessage}
                            defaultExpanded
                            isInternal={latestThreadMessage.is_internal}
                          />
                        </CardContent>
                      </Card>
                    ) : null}

                    <Card variant="outlined" sx={{ borderRadius: 2 }}>
                      <CardContent>
                        <Stack spacing={1.5}>
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                            <Typography variant="subtitle1">Reply Composer</Typography>
                            <ComposerStatusChips payload={selectedAnalysisPayload} />
                          </Stack>
                          <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
                            <Button
                              variant="outlined"
                              startIcon={<AutoAwesomeIcon />}
                              onClick={handleRegenerateDraft}
                              disabled={regenerateLoading || selectedRegenActive}
                            >
                              {selectedRegenActive ? 'Regenerating…' : regenerateLoading ? 'Queuing…' : 'Regenerate'}
                            </Button>
                            <Button
                              variant="outlined"
                              startIcon={<SaveIcon />}
                              onClick={handleSaveDraft}
                              disabled={!draftChanged || savingDraft}
                            >
                              {savingDraft ? 'Saving…' : 'Save draft'}
                            </Button>
                            <Button
                              variant="contained"
                              color="primary"
                              startIcon={<SendIcon />}
                              onClick={handleSendClick}
                              disabled={
                                sendingReply || !draftValue.trim() || !replyFromValue || sendBlocked
                              }
                            >
                              {sendingReply ? 'Sending…' : 'Send reply'}
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<ScheduleSendIcon />}
                              onClick={(e) => setScheduleAnchor(e.currentTarget)}
                              disabled={!draftValue.trim() || !replyFromValue || sendBlocked}
                            >
                              Schedule
                            </Button>
                            <Popover
                              open={Boolean(scheduleAnchor)}
                              anchorEl={scheduleAnchor}
                              onClose={() => setScheduleAnchor(null)}
                              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                            >
                              <Stack sx={{ p: 1.5, minWidth: 180 }} spacing={0.5}>
                                <Typography variant="caption" fontWeight={600}>Schedule send</Typography>
                                {[
                                  { label: 'Tomorrow 9am', value: dayjs().add(1, 'day').hour(9).minute(0).second(0) },
                                  { label: 'Friday 9am', value: (() => { const fri = dayjs().day(5); return (fri.isBefore(dayjs()) ? fri.add(1, 'week') : fri).hour(9).minute(0).second(0); })() },
                                  { label: 'Monday 9am', value: (() => { const mon = dayjs().day(1); return (mon.isBefore(dayjs()) || mon.isSame(dayjs(), 'day') ? mon.add(1, 'week') : mon).hour(9).minute(0).second(0); })() },
                                  { label: 'In 2 hours', value: dayjs().add(2, 'hour') },
                                  { label: 'In 4 hours', value: dayjs().add(4, 'hour') },
                                ].map((opt) => (
                                  <MenuItem
                                    key={opt.label}
                                    dense
                                    onClick={() => handleScheduleReply(opt.value.toISOString())}
                                  >
                                    {opt.label} — {opt.value.format('ddd h:mm A')}
                                  </MenuItem>
                                ))}
                              </Stack>
                            </Popover>
                          </Stack>
                          {scheduledReplies.length > 0 && (
                            <Stack spacing={0.5} mt={0.5}>
                              {scheduledReplies.map((sr) => (
                                <Alert
                                  key={sr.id}
                                  severity="info"
                                  sx={{ py: 0, '& .MuiAlert-message': { width: '100%' } }}
                                  action={
                                    <Button size="small" color="inherit" onClick={() => handleCancelScheduled(sr.id)}>
                                      Cancel
                                    </Button>
                                  }
                                >
                                  <Typography variant="caption">
                                    Scheduled for {dayjs(sr.scheduled_at).format('ddd MMM D, h:mm A')} from {sr.from_email}
                                  </Typography>
                                </Alert>
                              ))}
                            </Stack>
                          )}
                          <Stack spacing={1}>
                            <TextField
                              select
                              label="From"
                              value={replyFromValue}
                              onChange={(event) => setReplyFromValue(event.target.value)}
                              helperText={
                                senderOptions.length === 0
                                  ? 'No sender addresses configured'
                                  : 'Choose which inbox to send from'
                              }
                              fullWidth
                              disabled={senderOptions.length === 0}
                            >
                              {senderOptions.map((sender) => (
                                <MenuItem key={sender} value={sender}>
                                  {sender}
                                </MenuItem>
                              ))}
                            </TextField>
                            <TextField
                              label="To"
                              value={replyToValue}
                              onChange={(event) => setReplyToValue(event.target.value)}
                              helperText="Defaults to the latest external sender"
                              fullWidth
                            />
                            <TextField
                              label="CC"
                              value={replyCcValue}
                              onChange={(event) => setReplyCcValue(event.target.value)}
                              helperText="Optional"
                              fullWidth
                            />
                            <TextField
                              label="Subject"
                              value={replySubject}
                              onChange={(event) => setReplySubject(event.target.value)}
                              fullWidth
                            />
                          </Stack>
                          {isLockedBudgetConfirmationDraft ? (
                            <Stack spacing={1} mt={1}>
                              <Alert severity="info">
                                Budget template is shown as sent to preserve exact HTML formatting.
                              </Alert>
                              <Box
                                sx={{
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  borderRadius: 1,
                                  p: 1.5,
                                  bgcolor: 'grey.50',
                                  maxHeight: 380,
                                  overflow: 'auto',
                                }}
                                dangerouslySetInnerHTML={{ __html: draftValue }}
                              />
                              <TextField
                                multiline
                                minRows={3}
                                fullWidth
                                label="Add text under budget table (optional)"
                                value={budgetNote}
                                onChange={(event) => setBudgetNote(event.target.value)}
                                placeholder="Type your additional line(s)..."
                              />
                            </Stack>
                          ) : selectedRegenActive ? (
                            <Stack spacing={1}>
                              <Alert severity="info" icon={<CircularProgress size={16} />}>
                                Regenerating this draft in the background. You can work on other emails; this
                                one shows &quot;Draft ready&quot; when it lands.
                              </Alert>
                              <Box
                                sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5, opacity: 0.5 }}
                                dangerouslySetInnerHTML={{ __html: draftValue }}
                              />
                            </Stack>
                          ) : (
                            <>
                              {selectedMessage?.draft_regen_status === 'failed' ? (
                                <Alert severity="warning" sx={{ mb: 1 }}>
                                  The last regeneration failed{selectedMessage.draft_regen_error ? `: ${selectedMessage.draft_regen_error}` : ''}. The draft below is unchanged.
                                </Alert>
                              ) : null}
                              <TextEditor
                                ref={draftEditorRef}
                                content={draftValue}
                                onUpdate={({ editor }) => setDraftValue(editor.getHTML())}
                                placeholder="Draft reply"
                              />
                            </>
                          )}
                          {isListening && voiceTarget === 'draft' && (
                            <Alert severity="info" sx={{ py: 0.25, mt: 0.5 }}>
                              Listening… you can keep typing while the mic is on. Click the mic button to stop.
                              {interimVoiceText && (
                                <Box component="span" sx={{ display: 'block', fontStyle: 'italic', opacity: 0.8, mt: 0.25 }}>
                                  {interimVoiceText}
                                </Box>
                              )}
                            </Alert>
                          )}
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap mt={1}>
                            <Button
                              size="small"
                              variant="text"
                              component="label"
                            >
                              Attach files
                              <input
                                type="file"
                                multiple
                                hidden
                                onChange={(e) => {
                                  setReplyAttachments((prev) => [...prev, ...Array.from(e.target.files)]);
                                  e.target.value = '';
                                }}
                              />
                            </Button>
                            <Tooltip title={isListening && voiceTarget === 'draft' ? 'Stop listening' : 'Dictate reply with voice'}>
                              <Button
                                size="small"
                                variant={isListening && voiceTarget === 'draft' ? 'contained' : 'text'}
                                color={isListening && voiceTarget === 'draft' ? 'error' : 'inherit'}
                                onClick={() => toggleVoiceInput('draft')}
                                disabled={isListening && voiceTarget !== 'draft'}
                                startIcon={isListening && voiceTarget === 'draft' ? <MicOffIcon /> : <MicIcon />}
                              >
                                {isListening && voiceTarget === 'draft' ? 'Stop' : 'Voice'}
                              </Button>
                            </Tooltip>
                            <Tooltip title="Polish rough text into a professional email using AI">
                              <span>
                                <Button
                                  size="small"
                                  variant="text"
                                  onClick={handlePolishDraft}
                                  disabled={polishLoading || !draftValue.trim()}
                                  startIcon={<AutoAwesomeIcon />}
                                >
                                  {polishLoading ? 'Composing…' : 'Polish with AI'}
                                </Button>
                              </span>
                            </Tooltip>
                            <Tooltip title="Polish as usual, but nothing you said gets contradicted, softened, dropped, or followed by a policy redirect. You can also just say 'keep my wording' or 'don't override me' in your notes.">
                              <FormControlLabel
                                sx={{ ml: 0, mr: 0 }}
                                control={(
                                  <Switch
                                    size="small"
                                    checked={keepWording}
                                    onChange={(event) => setKeepWording(event.target.checked)}
                                  />
                                )}
                                label={<Typography variant="caption">My message stands</Typography>}
                              />
                            </Tooltip>
                            {replyAttachments.map((file, idx) => (
                              <Chip
                                key={`${file.name}-${idx}`}
                                label={file.name}
                                size="small"
                                onDelete={() => setReplyAttachments((prev) => prev.filter((_, i) => i !== idx))}
                              />
                            ))}
                          </Stack>
                          <ComposerReport
                            report={composerReport}
                            onDismiss={() => setComposerReport(null)}
                          />
                          <StaffInstructionHistory key={selectedMessage.id} payload={selectedAnalysisPayload} />
                        </Stack>
                      </CardContent>
                    </Card>

                    <ThreadAgentPanel
                      ref={agentPanelRef}
                      threadId={selectedMessage.id}
                      onDraftUpdated={handleAgentDraftUpdated}
                      onUseDraft={handleAgentUseDraft}
                    />

                    {earlierThreadMessages.length > 0 ? (
                      <Card variant="outlined" sx={{ borderRadius: 2 }}>
                        <CardContent>
                          <Typography variant="subtitle1" mb={1.5}>
                            Earlier messages ({earlierThreadMessages.length})
                          </Typography>
                          {earlierThreadMessages.map((msg) => (
                            <ThreadMessage
                              key={msg.id}
                              msg={msg}
                              defaultExpanded={false}
                              isInternal={msg.is_internal}
                            />
                          ))}
                        </CardContent>
                      </Card>
                    ) : null}

                    <Card variant="outlined" sx={{ borderRadius: 2 }}>
                      <CardContent>
                        <Stack spacing={1.5}>
                          <Typography variant="subtitle1">Internal Notes</Typography>
                          {selectedMessage?.notes && selectedMessage.notes.length > 0 ? (
                            <Stack spacing={1}>
                              {selectedMessage.notes.map((note) => (
                                <Box key={note.id} p={1.5} borderRadius={1} bgcolor="grey.50">
                                  <Typography variant="body2">{note.body}</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {note.author_name || note.author_email || 'Unknown'} &middot;{' '}
                                    {dayjs(note.created_at).fromNow()}
                                  </Typography>
                                </Box>
                              ))}
                            </Stack>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              No internal notes yet.
                            </Typography>
                          )}
                          {selectedMessage ? (
                            <Stack direction="row" spacing={1}>
                              <TextField
                                size="small"
                                fullWidth
                                placeholder="Add a note..."
                                id="thread-note-input"
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    const body = e.target.value.trim();
                                    if (!body) return;
                                    clientInboxApi.createThreadNote(selectedMessage.id, { body })
                                      .then((res) => {
                                        setSelectedMessageDetail((prev) => ({
                                          ...prev,
                                          notes: [res.data, ...(prev?.notes || [])],
                                        }));
                                        e.target.value = '';
                                      });
                                  }
                                }}
                              />
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => {
                                  const input = document.getElementById('thread-note-input');
                                  const body = (input?.value || '').trim();
                                  if (!body) return;
                                  clientInboxApi.createThreadNote(selectedMessage.id, { body })
                                    .then((res) => {
                                      setSelectedMessageDetail((prev) => ({
                                        ...prev,
                                        notes: [res.data, ...(prev?.notes || [])],
                                      }));
                                      input.value = '';
                                    });
                                }}
                              >
                                Add
                              </Button>
                            </Stack>
                          ) : null}
                        </Stack>
                      </CardContent>
                    </Card>
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Stack>

      {/* Compose New Email Dialog */}
      <Dialog open={composeOpen} onClose={() => setComposeOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>New Email</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <Autocomplete
              size="small"
              options={clientOptions}
              loading={clientsLoading}
              onInputChange={handleClientSearch}
              getOptionLabel={(option) => option.label || ''}
              isOptionEqualToValue={(option, value) => String(option.value) === String(value.value)}
              renderInput={(params) => (
                <TextField {...params} label="Client" placeholder="Search clients..." />
              )}
              onChange={(_, option) => {
                const el = document.getElementById('compose-client-id');
                if (el) el.value = option ? String(option.value) : '';
              }}
            />
            <input type="hidden" id="compose-client-id" />
            <TextField
              select
              label="From"
              defaultValue={defaultSender}
              fullWidth
              size="small"
              id="compose-from"
            >
              {senderOptions.map((sender) => (
                <MenuItem key={sender} value={sender}>{sender}</MenuItem>
              ))}
            </TextField>
            <TextField label="To" fullWidth size="small" id="compose-to" placeholder="recipient@example.com" />
            <TextField label="CC" fullWidth size="small" id="compose-cc" placeholder="Optional" />
            <TextField label="Subject" fullWidth size="small" id="compose-subject" />
            <TextEditor
              content=""
              onUpdate={({ editor }) => {
                const el = document.getElementById('compose-body-value');
                if (el) el.value = editor.getHTML();
              }}
              placeholder="Write your email..."
            />
            <input type="hidden" id="compose-body-value" />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setComposeOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={composeSending}
            onClick={() => {
              const clientId = document.getElementById('compose-client-id')?.value;
              const fromEmail = document.getElementById('compose-from')?.value;
              const to = (document.getElementById('compose-to')?.value || '').split(',').map((s) => s.trim()).filter(Boolean);
              const cc = (document.getElementById('compose-cc')?.value || '').split(',').map((s) => s.trim()).filter(Boolean);
              const subject = document.getElementById('compose-subject')?.value || '';
              const body = document.getElementById('compose-body-value')?.value || '';
              if (!clientId || !to.length || !subject || !body) {
                message.error('Client, to, subject, and body are required.');
                return;
              }
              setComposeSending(true);
              clientInboxApi.composeEmail({ client: clientId, from_email: fromEmail, to, cc, subject, body })
                .then((res) => {
                  message.success('Email sent.');
                  setComposeOpen(false);
                  loadMessages();
                  loadDashboardSummary();
                  if (res.data?.id) {
                    setSelectedId(res.data.id);
                    setSearchParams({ thread: String(res.data.id) });
                  }
                })
                .catch((err) => {
                  console.error('Failed to compose email', err);
                  message.error(err?.response?.data?.detail || 'Failed to send email.');
                })
                .finally(() => setComposeSending(false));
            }}
          >
            {composeSending ? 'Sending...' : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Undo Send Banner */}
      {undoSendData ? (
        <Box
          sx={{
            position: 'fixed',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            bgcolor: '#323232',
            color: '#fff',
            px: 3,
            py: 1.5,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            zIndex: 1400,
            boxShadow: 6,
          }}
        >
          <Typography variant="body2" sx={{ color: '#fff' }}>Sending reply...</Typography>
          <Button
            size="small"
            sx={{ color: '#ffb74d', fontWeight: 700 }}
            onClick={() => {
              if (undoSendData?.timer) {
                clearTimeout(undoSendData.timer);
              }
              setUndoSendData(null);
              setSendingReply(false);
              message.success('Send cancelled.');
            }}
          >
            Undo
          </Button>
        </Box>
      ) : null}

      <AutonomyReportDialog open={autonomyOpen} onClose={() => setAutonomyOpen(false)} />

      <UnrunActionDialog
        staffAction={unrunSendAction}
        onClose={() => setUnrunSendAction(null)}
        onSendAnyway={() => {
          setUnrunSendAction(null);
          handleSendReply();
        }}
        onRun={(action) => {
          setUnrunSendAction(null);
          handleRunStaffAction(action);
        }}
      />

      {/* Forward Email Dialog */}
      <Dialog
        open={Boolean(pendingStaffAction)}
        onClose={() => setPendingStaffAction(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{pendingStaffAction?.label || 'Run action'}</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} pt={0.5}>
            <Typography variant="body2">
              {pendingStaffAction?.type === 'send_portal_invite' ? (
                <>
                  This emails a setup link to{' '}
                  <strong>{pendingStaffAction?.params?.email}</strong> right now, as{' '}
                  <strong>
                    {actionRoleOverrides[pendingStaffAction?.dedupe_key] || 'admin'}
                  </strong>
                  . It can&apos;t be unsent.
                </>
              ) : (
                pendingStaffAction?.title
              )}
            </Typography>
            {pendingStaffAction?.preflight?.message ? (
              <Typography variant="caption" color="text.secondary">
                {pendingStaffAction.preflight.message}
              </Typography>
            ) : null}
            {(Array.isArray(pendingStaffAction?.preflight?.warnings)
              ? pendingStaffAction.preflight.warnings.filter(Boolean)
              : []
            ).map((warning) => (
              <Typography key={warning} variant="caption" color="warning.dark">
                {warning}
              </Typography>
            ))}
            <Typography variant="caption" color="text.secondary">
              The reply draft will be regenerated afterwards so it reads as done.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingStaffAction(null)}>Cancel</Button>
          <Button
            variant="contained"
            disableElevation
            onClick={() => {
              const action = pendingStaffAction;
              setPendingStaffAction(null);
              executeStaffAction(action);
            }}
          >
            {pendingStaffAction?.preflight?.verb || 'Send'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={forwardOpen} onClose={() => setForwardOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Forward Email</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              select
              label="From"
              defaultValue={defaultSender}
              fullWidth
              size="small"
              id="forward-from"
            >
              {senderOptions.map((sender) => (
                <MenuItem key={sender} value={sender}>{sender}</MenuItem>
              ))}
            </TextField>
            <TextField label="To" fullWidth size="small" id="forward-to" placeholder="recipient@example.com" />
            <TextField label="CC" fullWidth size="small" id="forward-cc" placeholder="Optional" />
            <TextField
              label="Add a note (optional)"
              fullWidth
              size="small"
              multiline
              minRows={2}
              id="forward-body"
              placeholder="Add a message above the forwarded content..."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setForwardOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={forwardSending}
            onClick={() => {
              const fromEmail = document.getElementById('forward-from')?.value;
              const to = (document.getElementById('forward-to')?.value || '').split(',').map((s) => s.trim()).filter(Boolean);
              const cc = (document.getElementById('forward-cc')?.value || '').split(',').map((s) => s.trim()).filter(Boolean);
              const body = document.getElementById('forward-body')?.value || '';
              if (!to.length) {
                message.error('At least one recipient is required.');
                return;
              }
              setForwardSending(true);
              clientInboxApi.forwardThread(selectedMessage.id, { from_email: fromEmail, to, cc, body })
                .then(() => {
                  message.success('Email forwarded.');
                  setForwardOpen(false);
                })
                .catch((err) => {
                  console.error('Failed to forward', err);
                  message.error(err?.response?.data?.detail || 'Failed to forward email.');
                })
                .finally(() => setForwardSending(false));
            }}
          >
            {forwardSending ? 'Forwarding...' : 'Forward'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function ClientInboxPageWithBoundary() {
  return (
    <InboxErrorBoundary>
      <ClientInboxPage />
    </InboxErrorBoundary>
  );
}

export default ClientInboxPageWithBoundary;
